<template>
  <div class="news-manage">
    <div class="header">
      <h2>行业新闻管理</h2>
      <el-button type="primary" @click="showAddDialog">添加新闻</el-button>
    </div>

    <!-- 添加/编辑新闻表单 -->
    <el-dialog
      :title="form.id ? '编辑新闻' : '添加新闻'"
      v-model="dialogVisible"
      width="50%"
      :close-on-click-modal="false"
    >
      <el-form :model="form" label-width="100px" :rules="rules" ref="formRef">
        <el-form-item label="标题" prop="title">
          <el-input v-model="form.title" placeholder="请输入新闻标题" />
        </el-form-item>
        <el-form-item label="封面图" prop="cover_url">
          <el-input v-model="form.cover_url" placeholder="请输入封面图URL" />
          <div class="image-preview" v-if="form.cover_url">
            <el-image 
              :src="form.cover_url" 
              fit="cover"
              :preview-src-list="[form.cover_url]"
            />
          </div>
        </el-form-item>
        <el-form-item label="新闻链接" prop="link">
          <el-input v-model="form.link" placeholder="请输入新闻链接" />
        </el-form-item>
        <el-form-item label="来源" prop="source">
          <el-input v-model="form.source" placeholder="请输入来源（如：遥感观测）" />
        </el-form-item>
        <el-form-item label="简要描述" prop="summary">
          <el-input
            v-model="form.summary"
            type="textarea"
            :rows="3"
            placeholder="请输入简要描述"
          />
        </el-form-item>
      </el-form>
      <template #footer>
        <span class="dialog-footer">
          <el-button @click="dialogVisible = false">取消</el-button>
          <el-button type="primary" @click="submitNews" :loading="submitting">
            确定
          </el-button>
        </span>
      </template>
    </el-dialog>

    <!-- 新闻列表 -->
    <el-card class="news-list">
      <el-table :data="newsList" style="width: 100%" v-loading="loading">
        <el-table-column prop="id" label="ID" width="80" />
        <el-table-column prop="title" label="标题" min-width="200" show-overflow-tooltip />
        <el-table-column label="封面" width="120">
          <template #default="{ row }">
            <el-image
              v-if="row.cover_url"
              :src="row.cover_url"
              :preview-src-list="[row.cover_url]"
              fit="cover"
              class="news-cover"
            />
            <span v-else>无封面</span>
          </template>
        </el-table-column>
        <el-table-column prop="source" label="来源" width="120" show-overflow-tooltip />
        <el-table-column prop="created_at" label="创建时间" width="180">
          <template #default="{ row }">
            {{ formatDate(row.created_at) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="150" fixed="right">
          <template #default="{ row }">
            <el-button type="primary" link @click="editNews(row)">编辑</el-button>
            <el-button type="danger" link @click="deleteNews(row.id)">删除</el-button>
          </template>
        </el-table-column>
      </el-table>
    </el-card>
  </div>
</template>

<script>
import request from '@/utils/request'
import { ElMessage, ElMessageBox } from 'element-plus'
import { ref, onMounted } from 'vue'

export default {
  name: 'NewsManage',
  setup() {
    const newsList = ref([])
    const loading = ref(false)
    const submitting = ref(false)
    const dialogVisible = ref(false)
    const formRef = ref(null)

    const form = ref({
      id: null,
      title: '',
      summary: '',
      cover_url: '',
      link: '',
      source: '',
      admin_id: null
    })

    const rules = {
      title: [
        { required: true, message: '请输入新闻标题', trigger: 'blur' },
        { min: 2, max: 100, message: '长度在 2 到 100 个字符', trigger: 'blur' }
      ],
      link: [
        { required: true, message: '请输入新闻链接', trigger: 'blur' },
        { type: 'url', message: '请输入正确的URL地址', trigger: 'blur' }
      ],
      cover_url: [
        { type: 'url', message: '请输入正确的URL地址', trigger: 'blur' }
      ]
    }

    const fetchNews = async () => {
      loading.value = true
      try {
        const res = await request.get('/admin/content/news')
        newsList.value = res
      } catch (error) {
        console.error('获取新闻列表失败:', error)
        ElMessage.error('获取新闻列表失败')
      } finally {
        loading.value = false
      }
    }

    const submitNews = async () => {
      if (!formRef.value) return
      
      try {
        await formRef.value.validate()
        submitting.value = true
        
        // 获取管理员信息
        const adminInfo = JSON.parse(localStorage.getItem('adminInfo') || '{}')
        if (!adminInfo || !adminInfo.id) {
          throw new Error('未找到管理员信息，请重新登录')
        }

        // 准备提交的数据
        const submitData = {
          ...form.value,
          admin_id: adminInfo.id,
          created_at: form.value.created_at ? new Date(form.value.created_at).toISOString().slice(0, 19).replace('T', ' ') : null,
          updated_at: new Date().toISOString().slice(0, 19).replace('T', ' ')
        }
        
        if (form.value.id) {
          await request.put(`/admin/content/news/${form.value.id}`, submitData)
          ElMessage.success('更新成功')
        } else {
          await request.post('/admin/content/news', submitData)
          ElMessage.success('添加成功')
        }
        
        dialogVisible.value = false
        resetForm()
        fetchNews()
      } catch (error) {
        console.error('提交失败:', error)
        ElMessage.error(error.message || '操作失败')
      } finally {
        submitting.value = false
      }
    }

    const editNews = (news) => {
      form.value = { ...news }
      dialogVisible.value = true
    }

    const deleteNews = async (id) => {
      try {
        await ElMessageBox.confirm('确认删除该条新闻？', '提示', {
          type: 'warning'
        })
        await request.delete(`/admin/content/news/${id}`)
        ElMessage.success('删除成功')
        fetchNews()
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error)
          ElMessage.error('删除失败')
        }
      }
    }

    const resetForm = () => {
      form.value = {
        id: null,
        title: '',
        summary: '',
        cover_url: '',
        link: '',
        source: '',
        admin_id: null
      }
      if (formRef.value) {
        formRef.value.resetFields()
      }
      dialogVisible.value = false
    }

    const showAddDialog = () => {
      resetForm()
      dialogVisible.value = true
    }

    const formatDate = (date) => {
      return new Date(date).toLocaleString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit'
      })
    }

    onMounted(() => {
      fetchNews()
    })

    return {
      newsList,
      loading,
      submitting,
      dialogVisible,
      form,
      formRef,
      rules,
      fetchNews,
      submitNews,
      editNews,
      deleteNews,
      resetForm,
      formatDate,
      showAddDialog
    }
  }
}
</script>

<style scoped>
.news-manage {
  padding: 20px;
}

.header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
}

.header h2 {
  margin: 0;
  font-size: 20px;
  color: #303133;
}

.news-list {
  margin-top: 20px;
}

.news-cover {
  width: 80px;
  height: 60px;
  border-radius: 4px;
  object-fit: cover;
}

.image-preview {
  margin-top: 10px;
}

.image-preview .el-image {
  width: 200px;
  height: 120px;
  border-radius: 4px;
  object-fit: cover;
}

:deep(.el-dialog__body) {
  padding-top: 20px;
}

:deep(.el-form-item__label) {
  font-weight: 500;
}

:deep(.el-table) {
  margin-top: 10px;
}

:deep(.el-button--link) {
  padding: 4px 8px;
}
</style>
